import { useState } from "react";
import { Button, Modal, Panel, Typography } from "@/components/atoms";
import { useKyvEnabled } from "@/hooks";
import {
  useDeleteVeivoValidationMutation,
  useListVeivoValidationsQuery,
  useMintVeivoResumeTokenMutation,
  VeivoReason,
  VeivoValidationListItem,
} from "@/redux/apis/vehicle/veivoApi";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { addToast } from "@/utils";

/**
 * In-flight and needs-fixing vehicle verifications.
 *
 * These exist ONLY in Veivo until they pass — RideMinder deliberately holds no
 * partial vehicle rows, so without this the operator would have no way to see
 * that a verification was started, stalled, or rejected. It would simply look
 * like the vehicle was never added.
 *
 * Renders nothing when there is nothing in flight, so it is invisible to
 * operators who are not mid-onboarding rather than being permanently empty
 * furniture.
 */
export const KyvValidationsPanel = () => {
  // Nothing to list on the plate flow — a vehicle added that way never has a
  // validation — so don't ask, and don't render the furniture.
  const { enabled: kyvEnabled } = useKyvEnabled();

  // Refetched on every visit: a verification finishes in Veivo (and on the
  // operator's phone), which nothing here would otherwise hear about, so a
  // cached copy kept showing finished vehicles as still in progress.
  const { data: inProgress } = useListVeivoValidationsQuery("in_progress", {
    skip: !kyvEnabled,
    refetchOnMountOrArgChange: true,
  });
  const { data: needsFixing } = useListVeivoValidationsQuery("needs_fixing", {
    skip: !kyvEnabled,
    refetchOnMountOrArgChange: true,
  });

  // The add-vehicle flow's drafts have rows of their own (VeivoDraftRows).
  const inProgressItems = (inProgress?.items ?? []).filter((item) => item.createOn !== "confirm");
  const needsFixingItems = needsFixing?.items ?? [];

  if (!kyvEnabled || (!inProgressItems.length && !needsFixingItems.length)) return null;

  return (
    <Panel className="mb-4 flex flex-col gap-4">
      {Boolean(needsFixingItems.length) && (
        <section>
          <Typography variant="title">Verification needs attention</Typography>
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            These vehicles could not be verified or added. The note on each says what to do.
          </Typography>
          <ul className="mt-3 flex flex-col gap-3">
            {needsFixingItems.map((item) => (
              <ValidationRow key={item.validationId} item={item} tone="danger" />
            ))}
          </ul>
        </section>
      )}

      {Boolean(inProgressItems.length) && (
        <section>
          <Typography variant="title">Verification in progress</Typography>
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            Continue on your phone to finish adding these vehicles.
          </Typography>
          <ul className="mt-3 flex flex-col gap-3">
            {inProgressItems.map((item) => (
              <ValidationRow key={item.validationId} item={item} tone="neutral" />
            ))}
          </ul>
        </section>
      )}
    </Panel>
  );
};

const ValidationRow = ({ item, tone }: { item: VeivoValidationListItem; tone: "danger" | "neutral" }) => {
  const description = [item.manufacturer, item.modelYear].filter(Boolean).join(" · ");
  // Verified, but the car was already somebody's vehicle (DEV-1754). There is
  // nothing to retake, so the only thing left to do is dismiss it.
  const refused = item.status === "refused";
  const [mintResumeToken, { isLoading: isMinting }] = useMintVeivoResumeTokenMutation();
  const [deleteValidation, { isLoading: isDeleting, error: deleteError }] =
    useDeleteVeivoValidationMutation();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleDelete = () => {
    deleteValidation(item.validationId)
      .unwrap()
      .then(() => {
        setConfirmingDelete(false);
        addToast("success", "Verification deleted.");
      })
      .catch(() => {
        // Left open: the message explains why, and the most likely reason —
        // the verification already created a vehicle — is actionable.
      });
  };

  const handleResume = () => {
    // Opened here, synchronously, because a window.open AFTER the await no
    // longer counts as user-initiated and gets blocked as a popup.
    //
    // Deliberately without "noopener": that returns a null handle, and the
    // handle is what lets us navigate the tab once the token arrives. The
    // opener is severed below instead — same result, same-origin page.
    const tab = window.open("", "_blank");

    mintResumeToken(item.validationId)
      .unwrap()
      .then(({ validationId, sessionToken }) => {
        // The public verification page owns resuming: it is reachable from a
        // phone without an operator login, which is the whole point of the
        // handoff. The token rides in the FRAGMENT — never a query param, so it
        // stays out of server logs and browser history sent upstream.
        const base = (import.meta.env.VITE_RIDEMINDER_BASE_NAME || "").replace(/^\/|\/$/g, "");
        const url = new URL(`${base ? `/${base}` : ""}/veivo-verification`, window.location.origin);
        url.searchParams.set("veivoValidationId", validationId);
        url.hash = `veivoSessionToken=${sessionToken}`;

        if (tab) {
          tab.opener = null;
          tab.location.href = url.toString();
        } else {
          // Popup blocked: same tab beats going nowhere.
          window.location.href = url.toString();
        }
      })
      .catch((error) => {
        tab?.close();
        // 409: the car has since become somebody's vehicle, and the message
        // says so — far more useful than a generic retry prompt (DEV-1754).
        addToast(
          "danger",
          error?.status === 409 ? getErrorMessages(error)[0] : "Could not resume this verification. Please try again."
        );
      });
  };

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 rounded-lg bg-neutral-gray p-3">
      <div className="min-w-0">
        <Typography variant="paragraph" className="font-semibold">
          {description || "Vehicle"}
        </Typography>
        <Typography variant="small" className="text-neutral-dark-gray">
          {[...identityLine(item), item.country].join(" · ")}
        </Typography>

        {/* The reason codes are why this list is worth having: a generic
            "verification failed" gives the operator nothing to act on. */}
        {Boolean(item.reasonSummary?.length) && (
          <ul className="mt-2 flex flex-col gap-1">
            {item.reasonSummary?.map((reason, index) => (
              <li key={`${reason.code}-${index}`}>
                <Typography variant="small" className={tone === "danger" ? "text-danger" : undefined}>
                  {reasonLabel(reason)}
                </Typography>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {!refused && (
          <Button variant="secondary" size="sm" disabled={isMinting} onClick={handleResume}>
            {tone === "danger" ? "Retake" : "Continue"}
          </Button>
        )}
        <Button
          variant="tertiary"
          size="sm"
          disabled={isDeleting}
          onClick={() => setConfirmingDelete(true)}
        >
          Delete
        </Button>
      </div>

      <Modal
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        variant="web-small"
        title="Delete this verification?"
        description={
          deleteError
            ? getErrorMessages(deleteError)[0]
            : `The verification for ${identityPhrase(item)} will be removed, along with any photos already taken. You can start again from Add Vehicle.`
        }
        renderButtons={() => (
          <>
            <Button variant="tertiary" size="md" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="md" disabled={isDeleting} onClick={handleDelete}>
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </>
        )}
      />
    </li>
  );
};

/** A verification started from a plate has no VIN until the registration
 *  document is read (DEV-1786), so the plate names it until then. */
const identityLine = (item: VeivoValidationListItem): string[] => {
  if (item.vin) return [`VIN ${item.vin}`];
  if (item.registrationNumber) return [`Plate ${item.registrationNumber}`, "VIN pending"];
  return ["VIN pending"];
};

const identityPhrase = (item: VeivoValidationListItem): string => {
  if (item.vin) return `VIN ${item.vin}`;
  if (item.registrationNumber) return `plate ${item.registrationNumber}`;
  return "this vehicle";
};

/** Prefer the specific check over the raw detail, falling back to the detail so
 *  an unrecognised code still says something useful rather than nothing. */
const REASON_LABELS: Record<string, string> = {
  vin_match: "The VIN on the document does not match the VIN on the registration document",
  plate_match: "The number plate does not match across the photos and document",
  region_match: "The state on the document does not match the state selected",
  document_kind: "The photo does not look like the document requested",
  expiry: "The document's expiry date could not be read, or it has expired",
};

const reasonLabel = (reason: VeivoReason) => REASON_LABELS[reason.code] ?? reason.detail;
